import React, { FC, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { ThunkDispatchType } from 'app/redux/types';

import { useTranslation } from 'app/lang';
import { replacingHyphenOnDash } from 'app/lib/replaceDashSymbol';

import { ResolveByCancelInsert, resolveByCancelInsert } from '../../../actions/conflict';
import { InsertInQueueForAdd } from '../../../actions/entries';
import Error from '../../EntryComponents/Error';
import Name from '../../EntryComponents/Name';
import RemoveButton from '../../EntryComponents/RemoveButton';
import PriceDiff from '../../PriceDiff';

import dictionary from './Entry.dictionary.json';

import entryStyles from '../../EntryComponents/EntryComponents.module.scss';
import styles from './ConflictEntry.module.scss';

// ---

type DispatchProp = ThunkDispatchType<ResolveByCancelInsert>;

type ConflictEntryProps = {
  entry: InsertInQueueForAdd['payload'];
};

const ConflictEntry: FC<ConflictEntryProps> = ({ entry: { bet, stake, match } }) => {
  const { t } = useTranslation();
  const dispatch: DispatchProp = useDispatch();

  const betId = bet.entity.id;
  const betName = bet.entity.names.get('long');
  const stakeName = stake.entity.names.get('long');
  const matchName = match.entity.name;

  const removeEntryHandler = useCallback(() => {
    dispatch(resolveByCancelInsert(betId));
  }, [betId]);

  const replacedMatchName = replacingHyphenOnDash(matchName);

  return (
    <div className={styles.container}
         data-test="conflictedEntry-container">
      <header className={styles.header}
              data-test="conflictedEntry-header">
        <Name value={betName || t(dictionary.unknownBetName, { betId })}
              data-test="conflictedEntry-entryName"
              className={entryStyles.entryName} />

        <RemoveButton data-test="conflictedEntry-removeButton"
                      onClick={removeEntryHandler} />
      </header>

      <main className={styles.main}
            data-test="conflictedEntry-main">
        <Name value={stakeName}
              data-test="conflictedEntry-stakeName"
              className={entryStyles.stakeName} />

        <PriceDiff data-test="conflictedEntry-priceDiff"
                   prev={bet.price}
                   next={bet.entity.price} />
      </main>

      <Name value={replacedMatchName}
            data-test="conflictedEntry-matchName"
            className={entryStyles.matchName} />

      <Error message={t(dictionary.errorMessage)}
             data-test="conflictedEntry-error"
             className={styles.error} />
    </div>
  );
};

export default ConflictEntry;
